import { z } from 'zod';
import { directionSchema, sourceSchema, nodeKindSchema } from './nodes/types.js';
/** Roles a person can hold in a space, from most to least privileged. */
export const roleSchema = z.enum(['owner', 'editor', 'viewer']);
export const ROLE_RANK = { owner: 3, editor: 2, viewer: 1 };
export function roleAllows(actual, required) {
    return ROLE_RANK[actual] >= ROLE_RANK[required];
}
export const userSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    displayName: z.string().min(1).max(80),
    avatarUrl: z.string().url().nullable().default(null),
    createdAt: z.string().datetime(),
});
/**
 * A space is one shareable dashboard. Luuk owns his; his partner is a member
 * of it with whatever role he grants. Every node, entry and goal belongs to
 * exactly one space, which is also the unit of access control.
 */
/** Sign-in providers. No password is ever stored; identity is always delegated. */
export const identityProviderSchema = z.enum(['google']);
export const spaceSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(80),
    createdAt: z.string().datetime(),
    role: roleSchema,
});
export const nodeSchema = z.object({
    id: z.string(),
    spaceId: z.string(),
    typeKey: z.string(),
    kind: nodeKindSchema,
    /** Overrides the catalog title, so two "Weight" nodes can be told apart. */
    title: z.string().min(1).max(80),
    /** Per-instance overrides, e.g. the unit of a custom number. */
    config: z.record(z.unknown()).default({}),
    archivedAt: z.string().datetime().nullable().default(null),
    createdAt: z.string().datetime(),
});
export const entrySchema = z.object({
    id: z.string(),
    nodeId: z.string(),
    /** When the thing was true, not when it was typed in. */
    recordedAt: z.string().datetime(),
    /** Set for metric nodes; null for collection and document nodes. */
    value: z.number().nullable(),
    /** Structured payload for non-metric kinds. */
    data: z.record(z.unknown()).nullable(),
    note: z.string().max(2000).nullable(),
    source: sourceSchema,
    /**
     * The provider's own id for this record. Unique per node, which is what
     * makes repeated Withings syncs idempotent instead of duplicating history.
     */
    externalId: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const goalSchema = z.object({
    id: z.string(),
    nodeId: z.string(),
    title: z.string().min(1).max(120),
    targetValue: z.number(),
    /** Captured when the goal is set, so progress is measured from a fixed line. */
    baselineValue: z.number().nullable(),
    startDate: z.string().datetime(),
    targetDate: z.string().datetime().nullable(),
    direction: directionSchema,
    achievedAt: z.string().datetime().nullable().default(null),
    createdAt: z.string().datetime(),
});
/**
 * Progress lives here rather than in the API or the chart component so that a
 * number shown next to a goal means the same thing everywhere it appears.
 */
export function computeGoalProgress(goal, currentValue, now = new Date()) {
    const daysRemaining = goal.targetDate
        ? Math.ceil((new Date(goal.targetDate).getTime() - now.getTime()) / 86_400_000)
        : null;
    if (currentValue === null) {
        return { fraction: null, currentValue: null, remaining: null, achieved: false, daysRemaining };
    }
    /**
     * Which way counts as progress is a property of THIS GOAL, not of the
     * measurement type.
     *
     * The catalog calls weight 'neutral' on purpose — losing weight is not
     * inherently better than gaining it, and the app should not assume. But a
     * goal always has a direction: 95 kg from a starting 113 is downward, and 95
     * kg from a starting 80 is upward. Reading only the catalog got this exactly
     * backwards for every weight goal, reporting achieved before the owner had
     * started and not achieved once they had passed the target.
     *
     * With no baseline there is genuinely no way to tell, and `fraction` is null
     * for the same reason — there is nothing to measure progress from yet.
     */
    /**
     * A 'neutral' goal with no baseline has no knowable direction at all, and
     * guessing one is how a brand-new goal reports itself complete: with no
     * baseline, a target of 95 and a first reading of 113 satisfies "113 >= 95".
     */
    const directionKnown = goal.direction !== 'neutral' || goal.baselineValue !== null;
    const movingDown = goal.direction === 'lower-is-better' ||
        (goal.direction === 'neutral' &&
            goal.baselineValue !== null &&
            goal.targetValue < goal.baselineValue);
    const achieved = directionKnown
        ? movingDown
            ? currentValue <= goal.targetValue
            : currentValue >= goal.targetValue
        : false;
    const remaining = Math.abs(goal.targetValue - currentValue);
    if (goal.baselineValue === null) {
        // Achieved is knowable without a baseline when the direction is explicit;
        // the fraction never is, because there is nothing to have travelled from.
        return { fraction: achieved ? 1 : null, currentValue, remaining, achieved, daysRemaining };
    }
    const total = goal.targetValue - goal.baselineValue;
    // A goal set at a value already equal to the target is either done or
    // meaningless; treating it as complete avoids dividing by zero.
    if (total === 0)
        return { fraction: 1, currentValue, remaining, achieved: true, daysRemaining };
    const travelled = currentValue - goal.baselineValue;
    const fraction = Math.min(1, Math.max(0, travelled / total));
    return { fraction, currentValue, remaining, achieved, daysRemaining };
}
/**
 * What still has to happen, per week, for a goal to land on its deadline.
 *
 * Separate from `computeGoalProgress` on purpose. Progress answers "how far
 * have I come"; this answers "what does the rest of it cost me", which is a
 * different question and is only answerable when there is a deadline to
 * divide by. Keeping it out of `GoalProgress` means every existing caller
 * keeps the shape it already reads, and nothing has to pretend a null pace
 * is a kind of progress.
 */
/**
 * Which way a goal is pointing, from the only two numbers that can honestly
 * say it: where this person started, and where they said they want to be.
 *
 * 95 kg from a starting 113 is downward. 18% body fat from a starting 12% is
 * upward. The node catalog used to answer this instead, and used to win, so a
 * goal could be stored believing the opposite of what its own numbers said —
 * and somebody deliberately putting weight back on was told at every reading
 * that they were losing ground.
 *
 * With no baseline there is nothing to compare against and the answer is
 * 'neutral'. The app does not know, and guessing is precisely what this
 * replaced.
 *
 * Lives here rather than beside the route so the API, the seed and anything
 * later that needs it all resolve a direction the same way. Two callers with
 * two copies of this is how they come to disagree about the same two numbers.
 */
export function resolveDirection(baselineValue, targetValue) {
    if (baselineValue === null)
        return 'neutral';
    if (targetValue < baselineValue)
        return 'lower-is-better';
    if (targetValue > baselineValue)
        return 'higher-is-better';
    return 'neutral';
}
/**
 * A deadline closer than this is not divided into a weekly rate.
 *
 * "0.4 kg per week" stops being useful advice when there are two days left —
 * the number explodes toward the deadline and reads as a demand rather than
 * a pace. The caller shows the remaining amount and the date instead.
 */
const MIN_DAYS_FOR_A_WEEKLY_RATE = 7;
export function computeGoalPace(goal, currentValue, now = new Date()) {
    const progress = computeGoalProgress(goal, currentValue, now);
    const { daysRemaining, remaining, achieved } = progress;
    const none = { perWeek: null, direction: null, remaining, daysRemaining };
    // Nothing left to ask of someone who has arrived, and nothing to divide by
    // without a deadline or a reading.
    if (achieved || currentValue === null || remaining === null)
        return none;
    if (daysRemaining === null || daysRemaining < MIN_DAYS_FOR_A_WEEKLY_RATE)
        return none;
    // Derived from where the target sits relative to the reading, not read off
    // `goal.direction` — which is 'neutral' for every weight goal the catalog
    // describes, and so would answer "neither". By this point the goal is known
    // not to be achieved, so the two can't disagree: a target below the current
    // reading is still to be lost, one above it still to be gained.
    const direction = goal.targetValue < currentValue ? 'lose' : 'gain';
    return {
        perWeek: remaining / (daysRemaining / 7),
        direction,
        remaining,
        daysRemaining,
    };
}
//# sourceMappingURL=domain.js.map